import { Flavor } from "../../../pojo/Util";
import { CardConfig, DBCardConfig, RegexConfig } from "../../../services/card.service";

export class IdCardUtil {
    public static readonly characterSets = {
        numbers: "0-9",
        letters: "A-Z",
        separator: `!"#%&'()*+,\\-./:;<=>^`,
    };

    public static escapeRegex(re: string) {
        return re.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }

    public static partToRegex(part: RegexConfig): Flavor<string, "regex"> {
        if (part.exact.selected) {
            let re = IdCardUtil.escapeRegex(part.exact.text);
            if (part.optional) re = `(?:${re})?`;
            return `(${re})`;
        }
        let characters = "";
        if (part.characterSets.letters) characters += this.characterSets.letters;
        if (part.characterSets.numbers) characters += this.characterSets.numbers;
        if (part.characterSets.specific.selected)
            characters += IdCardUtil.escapeRegex(part.characterSets.specific.characters);
        let re = characters.length ? `[${characters}]` : "(?:)";
        if (part.length.type === "fixed") re += `{${part.length.min}}`;
        else if (part.length.type === "range") re += `{${part.length.min},${part.length.max}}`;
        else if (part.length.type === "any") re += `+`;
        if (part.optional) re = `(?:${re})?`;
        if (part.mapTo !== "none") re = `?<${part.mapTo}>${re}`;
        return `(${re})`;
    }

    public static fromDbCard(card: DBCardConfig): CardConfig {
        return {
            id: card.id,
            name: card.name,
            description: card.description,
            tracks: card.tracks?.map((track) =>
                track.map((part) => {
                    let length: RegexConfig["length"]["type"] = "any";
                    if (part.minLength !== undefined && part.maxLength !== undefined) {
                        if (part.minLength === part.maxLength) length = "fixed";
                        else length = "range";
                    }

                    const parsed: RegexConfig = {
                        regex: "",
                        label: part.label,
                        length: {
                            type: length,
                            min: part.minLength ?? 0,
                            max: part.maxLength ?? part.minLength ?? 0,
                        },
                        characterSets: {
                            letters: part.allowLetters,
                            numbers: part.allowDigits,
                            specific: {
                                selected: part.allowSpecific !== undefined,
                                characters: part.allowSpecific ?? "",
                            },
                        },
                        exact: {
                            selected: part.exact !== undefined,
                            text: part.exact ?? "",
                        },
                        optional: part.optional,
                        mapTo: part.mapTo ?? "none",
                    };
                    parsed.regex = IdCardUtil.partToRegex(parsed);

                    return parsed;
                }),
            ),
        };
    }

    public static toDbCard(card: CardConfig): DBCardConfig {
        return {
            id: card.id,
            name: card.name,
            description: card.description,
            tracks: card.tracks.map((track, t) =>
                track.map((part) => {
                    const max = part.length.type === "range" ? part.length.max : part.length.min;
                    const specific = part.characterSets.specific;
                    return {
                        regex: IdCardUtil.partToRegex(part),
                        label: part.label,
                        minLength: part.length.type === "any" ? undefined : part.length.min,
                        maxLength: part.length.type === "any" ? undefined : max,
                        allowDigits: part.characterSets.numbers,
                        allowLetters: part.characterSets.letters,
                        allowSpecific: specific.selected ? specific.characters : undefined,
                        exact: part.exact.selected ? part.exact.text : undefined,
                        optional: part.optional,
                        mapTo: part.mapTo ?? "none",
                    };
                }),
            ),
        };
    }
}
